import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { OnboardingStatusResponse, OnboardingSetupRequest } from '@/lib/schemas';

export const ONBOARDING_QUERY_KEY = ['onboarding', 'status'] as const;

export function useOnboardingStatus() {
  return useQuery<OnboardingStatusResponse>({
    queryKey: ONBOARDING_QUERY_KEY,
    queryFn: () => api.onboarding.getStatus(),
    staleTime: 5 * 60 * 1000,  // 5 minutes — only changes on wizard completion
    refetchInterval: 30 * 1000, // Poll every 30s to pick up auto-completions
    retry: false,               // Don't retry — 401/404 means not authed or no backend yet
  });
}

export function useSetupOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OnboardingSetupRequest) => api.onboarding.setup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
    },
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.onboarding.complete(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
    },
  });
}

export function useSkipOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.onboarding.skip(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
    },
  });
}
